import { BaseDataJson } from '@owc-types/BaseDataJSON';
import { CombinedInstance, DispatcherData, Method, State } from '@owc-types/ComponentInitializeDispatcher';
import {
  getComponentStateConnection,
  initializeComponentState,
  subscribeComponentState,
} from '@owc-utils/Store/owcComponent';
import { parseAttribute } from '@owc-utils/Slot/SlotAttributes';
import { handleDestroy } from '@/store';
import '@owc-utils/owcfe';
import { ComponentOptionsWithProps } from 'vue/types/v3-component-options';
import { getComponentData } from '@owc/aem-component-data-util';

export type OwcComponentMixinData<DataJson> = Partial<DispatcherData> & {
  slotsArray: Record<string, any>[];
  componentData: DataJson;
  parentId?: string;
  isComponentDataInitialized: boolean;
};

export type OwcComponentMixinProps = {
  componentId: string;
  componentParentId: string;
};

export type OwcComponent = ComponentOptionsWithProps<
  OwcComponentMixinProps,
  {},
  OwcComponentMixinData<BaseDataJson>,
  {},
  OwcComponentMixinMethods,
  {},
  {}
>;

export type OwcComponentMixinMethods = {
  setComponentData: (data: any) => void;
  getComponentId: Method<string>;
  getComponentParentId: Method<string | undefined>;
  getName: Method<string | undefined>;
  getContext: Method<CombinedInstance>;
  isSlotContainer: Method<boolean>;
  mapChildComponents: Method<void>;
  updateSlot: Method<void>;
  getHost: <T>() => T & Element;
  updateComponentData: Method<void>;
};

export const props = {
  componentId: {
    type: String,
    required: false,
  },
  componentParentId: {
    type: String,
    required: false,
  },
};

const nestedClassElement = 'webcomponent-nested';

const mixin: OwcComponent = {
  data() {
    return {
      slotsArray: [],
      componentData: {},
      parentId: undefined,
      isComponentDataInitialized: false,
    };
  },
  async beforeCreate() {
    await initializeComponentState();
  },
  async mounted() {
    const id = this.getComponentId();

    if (this.isBuildWc) {
      this.mapChildComponents();
    }
    if (id && !this.isSlotContainer()) {
      await this.updateComponentData();

      subscribeComponentState(id, (componentState: State) => {
        const data = componentState.find(component => component.id === id);
        if (data) {
          this.setComponentData(data.json);
          this.$set(this, 'parentId', data.parentId ?? '');
        }
      });

      document.addEventListener('component-updated', this.updateComponentData);
    }
  },
  beforeDestroy() {
    const id = this.getComponentId();
    if (id && !this.isSlotContainer()) {
      handleDestroy(id);

      document.removeEventListener('component-updated', this.updateComponentData);
    }
  },
  methods: {
    getHost<T>() {
      return (this.$el.getRootNode() as ShadowRoot).host as T & Element;
    },
    setComponentData: function(data: any) {
      this.$set(this, 'componentData', {
        ...this.componentData,
        ...data,
      });
      this.isComponentDataInitialized = true;
    },
    getComponentId: function() {
      // slot container is always a child of a ComponentInitializeDispatcher instance
      // and never has a component id, so it needs to be retrieved directly from the
      // parent component
      return this.getContext().componentId;
    },
    getComponentParentId: function() {
      return this.parentId;
    },
    getName: function() {
      return this.getContext().$options.name;
    },
    isSlotContainer: function() {
      return this.$options.name === 'owc-slot-container';
    },
    getContext: function() {
      // slot container is always a child of a ComponentInitializeDispatcher instance
      // and never has a component id, so it needs to be retrieved directly from the
      // parent component
      return (this.isSlotContainer() ? this.$parent : this) as CombinedInstance;
    },
    mapChildComponents: function() {
      const id = this.getComponentId();
      const componentName = this.getName();
      for (const [key, value] of Object.entries(this.$refs)) {
        if (key.includes('slot-')) {
          const slot = document.createElement('slot');
          const attributeName = (value as HTMLElement).getAttribute('data-name');
          if (attributeName) {
            slot.setAttribute('name', attributeName);
          }
          /*
           * This event will trigger when the content of the slot is placed in the DOM, so we can
           * pass the information to the child components through the passDataToSlot function
           */
          const slotData = parseAttribute(value as HTMLElement, 'slot-component-data');
          slot.addEventListener('slotchange', async function() {
            let elements = slot.assignedElements();
            const connection = await getComponentStateConnection();

            /**
             * AEM still wraps some child components in a div for reasons unknown.
             * Some claim that it helps with the parsys and is a requirement for nesting web components
             * But if you take a look at Tabs or the Hotspot you'll notice that the nested-webcomponent class
             * is on the child webcomponent himself, so we have a big inconsistency here
             * And that is why this change is required to ensure that data is properly
             * passed to children using the slot-data-attribute but only on some webcomponents. If we apply the
             * "does it have the nested webcomponent class injected by aem" solution, we will break all the components
             * that manage to avoid this wrapping overhead
             *
             * To ensures that nothing is breaking, this effect is only triggered if inside the slot the first component
             * is a div with the class "webcomponent-nested"
             *
             * https://jira.mercedes-benz.io/browse/OWCA-979
             */
            if (elements[0]?.tagName === 'DIV' && elements[0]?.classList.contains(nestedClassElement)) {
              elements = [...elements[0].children] as Element[];
            }

            const childComponents = elements
              .map(element => {
                const elementId = element.getAttribute('component-id');
                if (elementId) {
                  connection.addComponent({
                    id: elementId,
                    parentId: id,
                    json: slotData,
                    parentName: componentName,
                  });
                }
                return elementId;
              })
              .filter(Boolean);

            if (id) {
              connection.addComponent({
                id,
                childComponents,
              });
            }
          });
          (value as HTMLElement).append(slot);

          this.slotsArray.push({ refValue: value, slotContainer: slot });
        }
      }
    },
    updateSlot: function(): void {
      /*
       * This update method is used by components that need reactivity of slot attributes
       * like the Tabs component, that needs the slotName updated when a click is made.
       */
      if (this.isBuildWc) {
        this.slotsArray.forEach(item => {
          const attributeName = (item.refValue as HTMLElement).getAttribute('data-name');
          const value = item.slotContainer.getAttribute('name');
          // CINOWF-31194 - don't set attribute if already present, so as not to
          // create an endless loop of "slotchange" events on safari
          if (attributeName && attributeName !== value) {
            item.slotContainer.setAttribute('name', attributeName);
          }
        });
      }
    },
    async updateComponentData() {
      const id = this.getComponentId();
      const parentId = this.getComponentParentId();
      const name = this.getName();

      const rawData = getComponentData(id);
      if (window.top?.aemNamespace) {
        this.setComponentData(rawData);
      }

      const connection = await getComponentStateConnection();
      connection.updateComponent({
        id,
        json: rawData,
        parentId,
        name,
      });
    },
  },
};

export default mixin;
